import { i18n } from '@/i18n';
import { plugin as formKitPlugIn } from '@formkit/vue';
import { init } from '@sentry/vue';
import { createPinia } from 'pinia';
import { type Component, createApp } from 'vue';
import { safeHtmlDirective } from '../../shared/helpers/safe-html';
import { version } from '../package.json';
import AppLoader from './AppLoader.ce.vue';
import { defineCustomElement } from './defineCustomElementWithStyles';
import eventBusPlugin from './plugins/event-bus-plugin';

// Define release for Sentry
const release = `venue-manager-app@${version}`;

// Initialise Sentry
if (import.meta.env?.VITE_ENVIRONMENT && import.meta.env.VITE_SENTRY_DSN) {
    init({
        app: createApp(AppLoader as Component),
        dsn: String(import.meta.env.VITE_SENTRY_DSN ?? ''),
        release,
        environment: String(import.meta.env.VITE_ENVIRONMENT ?? ''),
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['*'],
        autoSessionTracking: false, // Disable session tracking, as we don't need it in the widget.
        defaultIntegrations: false, // To lock it down even more, disable global error handlers (see previous message), so you only track scoped errors.
        beforeSend(event) {
            // Only capture errors if they originate from inside the component
            const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];

            // Check if any of the frames are from the Constell app
            const isConstellApp = frames.some(frame =>
                typeof frame.filename === 'string'
                && frame.filename?.includes('constell.com')
                && frame.filename?.includes('constell-widget'));

            // If not, don't send the event
            return isConstellApp ? event : null;
        },
    });
}

// Create Pinia instance
const pinia = createPinia();

// This will define our component as a WebComponent, with a small hack to get our styles in there:
customElements.define('constell-widget', defineCustomElement(AppLoader, {
    plugins: [
        { plugin: eventBusPlugin },
        { plugin: formKitPlugIn },
        { plugin: i18n },
        { plugin: pinia },
    ],
    directives: { 'safe-html': safeHtmlDirective },
}));
